import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Stars from "@/components/site/Stars";
import { useLang } from "@/lib/i18n";
import { api, errDetail } from "@/lib/api";
import type { Yacht } from "@/types";

interface Props { yachtId?: string; onDone?: () => void }

export default function ReviewForm({ yachtId, onDone }: Props) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", country: "", rating: 5, comment: "", yacht_id: yachtId || "" });
  const { data: yachts = [] } = useQuery<Yacht[]>({ queryKey: ["yachts"], queryFn: async () => (await api.get("/yachts")).data, enabled: open && !yachtId });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (f.comment.trim().length < 10) return toast.error(t("rev.min"));
    setSaving(true);
    try {
      await api.post("/reviews", { ...f, yacht_id: f.yacht_id || null, lang });
      setSent(true);
      onDone?.();
    } catch (err) {
      toast.error(t(`err.${errDetail(err)}`));
    } finally {
      setSaving(false);
    }
  };
  const close = (o: boolean) => { setOpen(o); if (!o) { setSent(false); setF({ name: "", country: "", rating: 5, comment: "", yacht_id: yachtId || "" }); } };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="review-open-btn" className="inline-flex items-center rounded-full border border-[#0B192C] px-6 py-3 text-sm font-semibold text-[#0B192C] transition-colors hover:bg-[#0B192C] hover:text-white">{t("rev.write")}</button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="sm:max-w-lg" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{sent ? t("rev.okTitle") : t("rev.formTitle")}</DialogTitle></DialogHeader>
          {sent ? (
            <div data-testid="review-success">
              <p className="text-slate-600">{t("rev.okBody")}</p>
              <button onClick={() => close(false)} data-testid="review-close-btn" className="mt-6 w-full rounded-lg bg-[#0B192C] py-3 text-sm font-semibold text-white">{t("form.close")}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="review-form">
              <div className="space-y-1.5"><Label>{t("rev.rating")}</Label><div><Stars value={f.rating} size="h-7 w-7" onChange={(v) => setF({ ...f, rating: v })} testId="review-rating" /></div></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>{t("rev.name")}</Label><Input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name-input" /></div>
                <div className="space-y-1.5"><Label>{t("rev.country")}</Label><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="review-country-input" /></div>
              </div>
              {!yachtId && (
                <div className="space-y-1.5"><Label>{t("rev.yacht")}</Label>
                  <select value={f.yacht_id} onChange={(e) => setF({ ...f, yacht_id: e.target.value })} data-testid="review-yacht-select" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                    <option value="">{t("rev.noYacht")}</option>
                    {yachts.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-1.5"><Label>{t("rev.comment")}</Label><Textarea required rows={4} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="review-comment-input" /></div>
              <button type="submit" disabled={saving} data-testid="review-submit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B192C] py-3 text-sm font-semibold text-white disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}{t("rev.submit")}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
